import React, { useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  update_assets_data,
  lightDataUpdate,
  selected_object,
} from '../../../../../redux/Slice/gridSlice';
import {
  setSelectedLightId,
  updateActiveComponent,
} from '../../../../../redux/Slice/globalStates';
import {
  SubMenuPortal,
  SimpleAssetTooltip,
} from '../../../../../utils/gridUtils';
import * as THREE from 'three';
import AssetVisibilityToggle from './AssetVisibilityToggle';
import {
  isAssetVisible,
  resolveSceneObject,
} from '../../../../../utils/ThreeJsUtils/assetVisibilityUtils';
import {
  getCameraStableId,
  resolveSceneCameraObject,
} from '../../../../../utils/ThreeJsUtils/sceneCameraUtils';
import { isHierarchyRowSelected, isHierarchyItemMultiSelected, toggleHierarchyItemSelection } from '../../../../../utils/selectAllObjects';
import { getGlbHierarchyExpandKey } from '../../../../../components/gridpagefunction/MeshExtractation';
import { setSelectedParticle } from '../../../../../redux/Slice/particleSlice';
// Recursive component for rendering nested children at any level
export const RecursiveChildRenderer = ({
  item,
  type,
  level = 0,
  isLast = false,
  scene,
  findDirectChildrenAssets,
  expandedGLBs,
  setExpandedGLBs,
  select_object,
  selectObjectInScene,
  selectGLBMesh,
  selectedPop,
  setselectedPop,
  submenuPosition,
  setSubmenuPosition,
  setSelectedGLBMeshPop,
  setGLBMeshSubmenuPosition,
  // Light-specific popup state
  selectedLightPop,
  setSelectedLightPop,
  lightSubmenuPosition,
  setLightSubmenuPosition,
  isRenamingLight,
  setIsRenamingLight,
  renamingLight,
  setRenamingLight,
  newLightName,
  setNewLightName,
  getAssetIcon,
  iconsPath,
  shapesIcon,
  assetIcons,
  setDraggingAssetId,
  setHoverTargetAssetId,
  handleDropOnAsset,
  parentKey = '',
  glbRootId = null,
  // GLB-related props
  glbMeshes = {},
  extractMeshesFromGLB,
  setGLBMeshes,
  // Delete handlers
  handleRemoveLight,
  handleRemoveParticle,
  handleRemoveCamera,
  handleDuplicate,
  deleteAllChildren,
  cleanupParentReferences,
  dispatch,
  store,
  assets_data,
  light_data,
  particles,
  selectedObject,
  setSelectedObjectState,
  objectt,
  outSideRef,
  // Unified handlers
  handleUnifiedMenuClick,
  handleUnifiedRename,
  handleUnifiedDuplicate,
  handleUnifiedDelete,
  unifiedPopup,
  setUnifiedPopup,
  unifiedRename,
  setUnifiedRename,
  handleRenameSubmit,
  DeleteMesh,
  handleVisibilityToggle,
}) => {
  const selectedLightId = useSelector(
    (state) => state.global_state.selectedLightId
  );
  const selectedMeshesForGrouping = useSelector(
    (state) => state.assets_object.selectedMeshesForGrouping ?? []
  );

  // GLB hierarchy node: { id, name, object, parentGLB, children, hierarchyNode }
  const isGLBHierarchyNode = !!(
    item?.hierarchyNode &&
    item?.parentGLB &&
    item?.object
  );

  let itemId;
  if (item && item.object && item.parentGLB) {
    itemId = item.object?.name || item.name || item.id;
  } else if (type === 'camera') {
    // Scene cameras must use stable _id/id — not display name (multiple cameras can share "Camera")
    itemId = getCameraStableId(item);
  } else if (type === 'light') {
    // Lights use unique scene id — display names like "Spot Light 1" are not scene names
    itemId = item.id || item._id;
  } else if (type === 'particle') {
    itemId = item.id || item._id;
  } else {
    // Regular asset
    itemId =
      item._id ||
      item?.url ||
      item?.name ||
      item?.id ||
      item?.userData?.id ||
      item?.userData?.name;
  }
  // if(type === 'light'||type==='particle'){
  //   itemId = item._id || item.id;
  // }

  const itemObj =
    resolveSceneObject(scene, item, type) ||
    (itemId ? scene.getObjectByName(itemId) : null);
  // Check if this is a GLB object
  const isGLB =
    type === 'mesh' &&
    (item?.file_name?.toLowerCase().includes('.glb') ||
      item?.url?.toLowerCase().includes('.glb'));

  const glbId = itemId;
  const effectiveGlbRootId =
    glbRootId ||
    (isGLB ? glbId : null) ||
    item?.parentGLB?._id ||
    item?.parentGLB?.name ||
    null;
  const hasHierarchyChildren =
    isGLBHierarchyNode && item.children?.length > 0;
  const hasGLBChildren =
    isGLB &&
    !isGLBHierarchyNode &&
    glbMeshes[glbId] &&
    glbMeshes[glbId].length > 0;

  const hasAssetChildren = itemObj
    ? findDirectChildrenAssets(itemObj).length > 0
    : false;

  const hasChildren =
    hasAssetChildren || hasGLBChildren || hasHierarchyChildren;

  const expandKey =
    isGLBHierarchyNode || (item?.object && item?.parentGLB)
      ? getGlbHierarchyExpandKey(
        effectiveGlbRootId,
        item.id || item.object?.uuid || itemId
      )
      : itemId;

  // Ref for rename input
  const renameInputRef = useRef(null);

  // Effect to focus and set cursor position when renaming starts
  useEffect(() => {
    if (
      unifiedRename.isRenaming &&
      unifiedRename.assetId === itemId &&
      renameInputRef.current
    ) {
      renameInputRef.current.focus();
      renameInputRef.current.setSelectionRange(
        unifiedRename.newName.length,
        unifiedRename.newName.length
      );
    }
  }, [unifiedRename.isRenaming, unifiedRename.assetId, itemId]);

  const isExpanded = expandedGLBs.has(expandKey);

  // Calculate indentation based on level
  const paddingLeft = 24 + level * 28; // 39px base + 18px per level
  const lineLeft = 7 + level * 28; // 21px base + 18px per level

  const handleToggle = (e) => {
    e.stopPropagation();
    const newSet = new Set(expandedGLBs);
    if (newSet.has(expandKey)) {
      newSet.delete(expandKey);
    } else {
      newSet.add(expandKey);

      if (isGLB && extractMeshesFromGLB && setGLBMeshes && !glbMeshes[glbId]) {
        const meshes = extractMeshesFromGLB(item);
        if (meshes.length > 0) {
          setGLBMeshes((prev) => ({
            ...prev,
            [glbId]: meshes,
          }));
        }
      }
    }
    setExpandedGLBs(newSet);
  };

  const handleClick = (e) => {
    if (e?.ctrlKey || e?.metaKey) {
      e.stopPropagation();
      toggleHierarchyItemSelection({
        scene,
        item,
        type,
        transformControls: objectt?.transformControls,
        onPrimarySelected: setSelectedObjectState,
      });
      return;
    }

    if (type === 'mesh' && item?.object && item?.parentGLB) {
      selectGLBMesh(item);
      return;
    }
    if (type === 'mesh') {
      let objectName = itemId;
      if (!scene.getObjectByName(objectName)) {
        objectName = item._id || item?.url || item?.name || item?.id;
        if (!scene.getObjectByName(objectName)) {
          objectName = item?.userData?.id || item?.userData?.name;
        }
      }
      selectObjectInScene(objectName);
    } else if (type === 'camera') {
      const cameraId = getCameraStableId(item);
      if (!cameraId) return;
      let cameraObj =
        resolveSceneCameraObject(scene, item) ||
        scene.getObjectByName(cameraId);
      if (!cameraObj) {
        cameraObj = new THREE.PerspectiveCamera(
          item.fov ?? 50,
          window.innerWidth / window.innerHeight,
          item.near || 0.1,
          item.far ?? 10
        );
        cameraObj.name = cameraId;
        if (item.position) {
          cameraObj.position.set(
            item.position.x ?? 0,
            item.position.y ?? 0,
            item.position.z ?? 0
          );
        }
        scene.add(cameraObj);
      }
      // Do NOT override position from Redux here — the scene object already has the correct transform.
      selectObjectInScene(cameraObj);
      store.dispatch(selected_object(cameraObj));
      if (objectt && typeof objectt.showCameraHelper === 'function') {
        objectt.showCameraHelper(true, cameraObj, cameraId);
      }
      dispatch(updateActiveComponent({ option: 'Camera', type: 'add' }));
      dispatch(updateActiveComponent({ option: 'Environment', type: 'add' }));
    } else if (type === 'light') {
      const lightId = item.id || item._id;
      if (!lightId) return;

      let lightObj = scene.getObjectByName(lightId);
      if (!lightObj) {
        scene.traverse((child) => {
          if (!lightObj && child.isLight && child.name === lightId) {
            lightObj = child;
          }
        });
      }

      if (lightObj) {
        dispatch(setSelectedLightId(lightId));
        dispatch(updateActiveComponent({ option: 'Light', type: 'add' }));
        selectObjectInScene(lightObj);
      }
    } else if (type === 'particle') {
      const particleObj =
        scene.getObjectByName(itemId) ||
        scene.getObjectByName(item._id || item.id || item.name);
      if (particleObj) {
        selectObjectInScene(particleObj);
        setSelectedObjectState(particleObj);
        objectt?.transformControls?.attach(particleObj);
      } else {
        selectObjectInScene(itemId);
      }
      dispatch(setSelectedParticle(itemId));
    } else if (type === 'empty') {
      selectObjectInScene(itemId);
      const obj = scene.getObjectByName(itemId);
      if (obj) {
        dispatch(selected_object(obj));
        setSelectedObjectState(obj);
      }
    } else {
      let objectName = itemId;
      if (!scene.getObjectByName(objectName)) {
        objectName = item._id || item?.url || item?.name || item?.id;
        if (!scene.getObjectByName(objectName)) {
          objectName = item?.userData?.id || item?.userData?.name;
        }
      }
      selectObjectInScene(objectName);
    }
  };

  const handleMenuClick = (e) => {
    // Use unified popup system for all asset types
    handleUnifiedMenuClick(e, itemId, type, item);
  };

  const handleDragStart = (e) => {
    if (
      type === 'hotspot' ||
      item?.deleteType === 'hotspot' ||
      item?.userData?.isHotspot
    ) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    setDraggingAssetId(itemId);

    // ✅ Get icon using the same function that displays it
    const getDragIcon = () => {
      if (type === 'mesh') {
        return getAssetIcon(item, 'mesh', scene); // Will check geometry
      } else if (type === 'camera') {
        return iconsPath?.group252Image || iconsPath?.cameraIcon;
      } else if (type === 'empty') {
        return iconsPath?.emptyIcon || iconsPath?.group252Image;
      } else if (type === 'light') {
        if (item?.light_type === 'Ambient Light')
          return iconsPath?.ambientlight;
        if (item?.light_type === 'Directional Light')
          return iconsPath?.Light2Image;
        if (item?.light_type === 'Spot Light') return iconsPath?.Light3Image;
        if (item?.light_type === 'Area Light') return iconsPath?.arealight;
        if (item?.light_type === 'Point Light') return iconsPath?.Light1Image;
        return iconsPath?.group26Image;
      } else if (type === 'particle') {
        return getAssetIcon(item, 'particle'); // ← ADD THIS BEFORE the else
      } else {
        return getAssetIcon(item, type || 'default', scene);
      }
    };

    const dragData = {
      id: itemId,
      name: item?.file_name || item?.name || item?.asset_file_name || item?.temp_file_name || 'Asset',
      type: type || 'asset',
      icon: getDragIcon(),
      properties: {
        position: true,
        scale: true,
        rotation: true,
      },
    };

    if (isGLB) {
      dragData.isGLB = true;
      dragData.glbId = glbId;
      dragData.hasChildren = hasChildren;
    }

    e.dataTransfer.setData('application/json', JSON.stringify(dragData));
  };

  const handleDragOver = (e) => {
    if (
      type === 'hotspot' ||
      item?.deleteType === 'hotspot' ||
      item?.userData?.isHotspot
    ) {
      return;
    }
    e.preventDefault();
    setHoverTargetAssetId(itemId);
  };

  const handleDragLeave = () => {
    setHoverTargetAssetId((prev) => (prev === itemId ? null : prev));
  };

  const handleDrop = (e) => {
    if (
      type === 'hotspot' ||
      item?.deleteType === 'hotspot' ||
      item?.userData?.isHotspot
    ) {
      e.preventDefault();
      return;
    }
    handleDropOnAsset(item, e);
  };

  let isSelected = isHierarchyRowSelected(
    select_object,
    selectedMeshesForGrouping,
    { item, type, scene },
    selectedLightId
  );

  const inMultiSelect = isHierarchyItemMultiSelected(
    selectedMeshesForGrouping,
    { item, type, scene }
  );

  if (type === 'light' && !inMultiSelect) {
    const visible = isAssetVisible(
      item,
      itemObj || resolveSceneObject(scene, item, type)
    );
    isSelected = visible && isSelected;
  }

  return (
    <React.Fragment>
      <li
        className="relative"
        draggable={
          type !== 'hotspot' &&
          item?.deleteType !== 'hotspot' &&
          !item?.userData?.isHotspot
        }
        onDragStart={handleDragStart}
        onDragEnd={() => setDraggingAssetId(null)}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Vertical line */}
        <div
          className="absolute w-[1px] bg-[#444447] z-[1]"
          style={{
            left: `${lineLeft}px`,
            top: 0,
            // When a GLB is expanded, stop the parent connector line at mid-row
            // so it doesn't continue below the expand/collapse box. When collapsed,
            // keep the full line so it connects to the next sibling.
            height:
              isLast || (isGLB && hasChildren && isExpanded) ? '50%' : '100%',
          }}
        />
        {/* Horizontal line - hide for ANY item with children */}
        {!hasChildren && (
          <div
            className="absolute w-[14px] h-[1px] bg-[#444447] z-[1]"
            style={{
              left: `${lineLeft}px`,
              top: '50%',
            }}
          />
        )}

        {/* Main content */}
        {/* Main content */}
        <div
          onClick={handleClick}
          data-object-uuid={itemObj?.uuid || item?.uuid}
          data-object-name={itemObj?.name || item?.name}
          className={`group cursor-pointer flex justify-between min-w-full w-max text-white py-1 rounded relative z-[2] pr-0 ${isSelected ? 'bg-[#ffffff1a]' : ''
            }`}
          style={{ paddingLeft: `${paddingLeft}px` }}
        >
          <div className="flex items-center gap-[6px] relative left-[2px] flex-1 min-w-0">
            {/* Toggle button */}
            {hasChildren && (
              <button
                onClick={handleToggle}
                className="w-3 h-3 flex absolute items-center justify-center border border-[#444447] z-[9999] rounded-sm bg-[#1d1e21]"
                style={{
                  left: `${lineLeft - paddingLeft - 6}px`, // Position relative to paddingLeft
                }}
              >
                <img
                  src={iconsPath.rightIcon}
                  alt="Toggle"
                  className={`w-2 h-2 transform transition-transform duration-200 ${isExpanded ? 'rotate-90' : 'rotate-0'
                    }`}
                />
              </button>
            )}

            {/* Icon */}
            {(() => {
              const iconSrc = (() => {
                if (type === 'mesh') {
                  return getAssetIcon(item, 'mesh', scene);
                } else if (type === 'camera') {
                  return iconsPath?.group252Image || iconsPath?.cameraIcon;
                } else if (type === 'empty') {
                  return iconsPath?.emptyIcon || iconsPath?.group252Image;
                } else if (type === 'light') {
                  if (item?.light_type === 'Ambient Light') {
                    return iconsPath?.ambientlight;
                  } else if (item?.light_type === 'Directional Light') {
                    return iconsPath?.Light2Image;
                  } else if (item?.light_type === 'Spot Light') {
                    return iconsPath?.Light3Image;
                  } else if (item?.light_type === 'Area Light') {
                    return iconsPath?.arealight;
                  } else if (item?.light_type === 'Point Light') {
                    return iconsPath?.Light1Image;
                  } else {
                    return iconsPath?.group26Image;
                  }
                } else if (type === 'particle') {
                  return getAssetIcon(item, 'particle');
                } else {
                  return getAssetIcon(item, type || 'default', scene);
                }
              })();
              return (
                <img
                  src={iconSrc}
                  className="w-[16px] h-[16px] ml-[4px] object-contain flex-shrink-0"
                  alt={`${type || 'asset'} icon`}
                />
              );
            })()}

            {/* Name or Rename input */}
            {/* Name or Rename input */}
            {unifiedRename.isRenaming && unifiedRename.assetId === itemId ? (
              <input
                ref={renameInputRef}
                type="text"
                value={unifiedRename.newName}
                onChange={(e) =>
                  setUnifiedRename({
                    ...unifiedRename,
                    newName: e.target.value,
                  })
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleRenameSubmit();
                  } else if (e.key === 'Escape') {
                    setUnifiedRename({
                      isRenaming: false,
                      assetId: null,
                      assetType: null,
                      newName: '',
                      assetItem: null,
                    });
                  }
                }}
                onBlur={() => {
                  if (unifiedRename.newName.trim()) {
                    handleRenameSubmit();
                  } else {
                    setUnifiedRename({
                      isRenaming: false,
                      assetId: null,
                      assetType: null,
                      newName: '',
                      assetItem: null,
                    });
                  }
                }}
                className="w-full bg-[#444447] text-white text-[10px] px-2 py-1 rounded focus:outline-none ml-[4px]"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <SimpleAssetTooltip
                label={(() => {
                  if (type === 'mesh') {
                    if (item?.object && item?.parentGLB) {
                      return (
                        item.name || item.object?.name || item.id || 'Mesh'
                      );
                    }
                    return item?.file_name || item?.asset_file_name || 'Mesh';
                  } else if (type === 'camera') {
                    return item?.file_name || item?.temp_file_name || 'Camera';
                  } else if (type === 'light') {
                    return item?.file_name || item?.temp_file_name || 'Light';
                  } else if (type === 'empty') {
                    return item?.file_name || item?.temp_file_name || 'Empty';
                  } else if (type === 'particle') {
                    return (
                      item?.file_name || item?.temp_file_name || 'Particle'
                    );
                  }
                  return (
                    item?.file_name ||
                    item?.asset_file_name ||
                    item?.temp_file_name ||
                    'Asset'
                  );
                })()}
              >
                <div className="flex items-center min-w-0 pr-2">
                  <span
                    className="text-left text-[10px] text-gray-300 ml-[4px] truncate"
                    style={{
                      whiteSpace: 'nowrap',
                      display: 'block',
                      maxWidth: 'calc(140px + var(--hierarchy-scroll-left, 0px))',
                    }}
                  >
                    {(() => {
                      if (type === 'mesh') {
                        if (item?.object && item?.parentGLB) {
                          return (
                            item.name || item.object?.name || item.id || 'Mesh'
                          );
                        }
                        return item?.file_name || item?.asset_file_name || 'Mesh';
                      } else if (type === 'camera') {
                        return (
                          item?.name || item?.file_name || item?.temp_file_name || 'Camera'
                        );
                      } else if (type === 'light') {
                        return item?.name || item?.file_name || item?.temp_file_name || 'Light';
                      } else if (type === 'empty') {
                        return item?.name || item?.file_name || item?.temp_file_name || 'Empty';
                      } else if (type === 'particle') {
                        return (
                          item?.name || item?.file_name || item?.temp_file_name || 'Particle'
                        );
                      }
                      return (
                        item?.file_name ||
                        item?.asset_file_name ||
                        item?.temp_file_name ||
                        'Asset'
                      );
                    })()}
                  </span>
                </div>
              </SimpleAssetTooltip>
            )}
          </div>

          {/* Options menu — visibility eye only on parent (root) assets */}
          <div
            className={`sticky right-0 flex-shrink-0 z-[10] flex items-center pl-2 pr-1 ${
              level === 0
                ? 'justify-between min-w-[46px]'
                : 'justify-end min-w-[24px]'
            } ${isSelected ? 'bg-[#2b2c2f]' : 'bg-[#131418]'}`}
          >
            {level === 0 && handleVisibilityToggle && (
              <AssetVisibilityToggle
                visible={isAssetVisible(item, itemObj || resolveSceneObject(scene, item, type))}
                onToggle={() =>
                  handleVisibilityToggle(item, type, type, itemId)
                }
              />
            )}
            <img
              onClick={(e) => {
                e.stopPropagation(); // Prevent bubbling to avoid double selection
                // First select the item
                handleClick();
                // Then open the menu
                handleMenuClick(e);
              }}
              src={iconsPath.threeDots}
              className="w-[12px] cursor-pointer hover:opacity-70 flex-shrink-0"
              alt="Options"
            />
          </div>
        </div>

      </li>

      {/* Unified context menu for all asset types */}
      {unifiedPopup.isOpen && unifiedPopup.assetId === itemId && (
        <SubMenuPortal position={unifiedPopup.position}>
          <div
            ref={outSideRef}
            className="absolute top-0 -right-[6rem] bg-[#1d1e21] text-white text-[10px] rounded shadow-md w-[85px] mt-[10px] text-left !z-[99999999999] flex flex-col"
          >
            <button
              onClick={() => handleUnifiedRename(itemId, type, item)}
              className="py-[4px] px-[14px] text-left hover:bg-[#2a2a2a]"
            >
              Rename
            </button>
            <button
              onClick={() => handleUnifiedDuplicate(itemId, type, item)}
              className="py-[4px] px-[14px] text-left hover:bg-[#2a2a2a]"
            >
              Duplicate
            </button>
            <button
              onClick={() => handleUnifiedDelete(itemId, type, item)}
              className="py-[4px] px-[14px] text-left hover:bg-[#2a2a2a]"
            >
              Delete
            </button>
          </div>
        </SubMenuPortal>
      )}

      {/* Recursively render children */}
      {hasChildren && isExpanded && (
        <div className="relative">
          {(() => {
            const children = [];
            const processedSceneUuids = new Set();
            const nestedAssetSubtreeUuids = new Set();

            // 1. For Asset children (including particles) - PROCESS FIRST to prioritize original names
            if (hasAssetChildren && itemObj) {
              const assetChildren = findDirectChildrenAssets(itemObj);
              const processedChildrenIds = new Set();
              const processedSceneUUIDs = new Set();

              assetChildren.forEach(({ item: childItem }) => {
                const childObj =
                  scene.getObjectByName(childItem._id) ||
                  scene.getObjectByName(childItem.id) ||
                  scene.getObjectByName(childItem.name) ||
                  scene.getObjectByName(childItem.url);
                if (childObj) {
                  childObj.traverse((o) => nestedAssetSubtreeUuids.add(o.uuid));
                }
              });

              assetChildren.forEach(
                ({ item: childItem, type: childType }, index) => {
                  // Collect ALL possible identifiers for this child
                  const childId =
                    childItem._id ||
                    childItem.id ||
                    childItem.name ||
                    childItem.url;
                  const childObj =
                    (childItem._id
                      ? scene.getObjectByName(childItem._id)
                      : null) ||
                    (childItem.id
                      ? scene.getObjectByName(childItem.id)
                      : null) ||
                    (childItem.name
                      ? scene.getObjectByName(childItem.name)
                      : null) ||
                    (childItem.url
                      ? scene.getObjectByName(childItem.url)
                      : null) ||
                    (childItem.temp_file_name
                      ? scene.getObjectByName(childItem.temp_file_name)
                      : null);
                  const childUuid = childObj?.uuid;

                  // Also find the actual scene object to get its UUID
                  const childSceneObj =
                    scene.getObjectByName(childItem._id) ||
                    scene.getObjectByName(childItem.id) ||
                    scene.getObjectByName(childItem.name);

                  const childUUID = childSceneObj?.uuid;

                  // Skip if already processed by UUID (most reliable)
                  if (childUUID && processedSceneUUIDs.has(childUUID)) {
                    return;
                  }

                  // Skip if already processed by ID
                  if (childId && processedChildrenIds.has(childId)) {
                    return;
                  }

                  // Mark as processed
                  if (childUUID) processedSceneUUIDs.add(childUUID);
                  if (childId) processedChildrenIds.add(childId);

                  // Also add all alternate IDs
                  [childItem._id, childItem.id, childItem.name]
                    .filter(Boolean)
                    .forEach((id) => {
                      processedChildrenIds.add(id);
                    });

                  children.push({
                    type: childType,
                    data: childItem,
                    index: children.length,
                    isLast: false,
                    key: `${itemId}-asset-child-${childUUID || childId || index}`,
                    onClick: () =>
                      selectObjectInScene(
                        childItem._id || childItem?.url || childItem?.name
                      ),
                    onMenuClick: (e) => {
                      e.stopPropagation();
                      setselectedPop(childItem._id || childItem?.name);
                      const rect = e.currentTarget.getBoundingClientRect();
                      setSubmenuPosition({
                        top: rect.top - 10,
                        left: rect.right + 10,
                      });
                    },
                    isSelected:
                      select_object?.uuid === childItem.id ||
                      select_object?.name === childItem.name ||
                      select_object?._id === childItem._id,
                    icon: getAssetIcon(childItem, childType),
                    name:
                      childItem?.file_name ||
                      childItem?.name ||
                      childItem?.url ||
                      'Unknown',
                    alt: `${childType} icon`,
                  });
                }
              );

              // Update the parent's processed objects set to include these children
              if (processedSceneUuids) {
                processedSceneUUIDs.forEach(uuid => processedSceneUuids.add(uuid));
              }
            }

            // 2. GLB object graph children (parent/child structure from the file)
            const appendHierarchyNode = (node, keyPrefix) => {
              const meshUuid = node.object?.uuid;
              if (meshUuid && processedSceneUuids.has(meshUuid)) return;

              if (meshUuid && nestedAssetSubtreeUuids.has(meshUuid)) return;
              let ancestor = node.object?.parent;
              while (ancestor) {
                if (nestedAssetSubtreeUuids.has(ancestor.uuid)) return;
                ancestor = ancestor.parent;
              }

              if (meshUuid) processedSceneUuids.add(meshUuid);
              children.push({
                type: 'mesh',
                data: node,
                index: children.length,
                isLast: false,
                key: `${keyPrefix}-${meshUuid || node.id}`,
                onClick: () => selectGLBMesh(node),
                onMenuClick: (e) => {
                  e.stopPropagation();
                  setSelectedGLBMeshPop(node.id);
                  const rect = e.currentTarget.getBoundingClientRect();
                  setGLBMeshSubmenuPosition({
                    top: rect.top - 10,
                    left: rect.right + 10,
                  });
                },
                isSelected:
                  select_object?.uuid === node.object?.uuid ||
                  select_object?.uuid === node.id,
                icon: shapesIcon?.cube || assetIcons?.mesh,
                name: node.name || node.object?.name || 'Object',
                alt: `${node.nodeType || 'object'} icon`,
              });
            };

            if (hasHierarchyChildren) {
              item.children.forEach((childNode, index) => {
                appendHierarchyNode(
                  childNode,
                  `${expandKey}-child-${index}`
                );
              });
            } else if (isGLB && hasGLBChildren && glbMeshes[glbId]) {
              glbMeshes[glbId].forEach((node, index) => {
                appendHierarchyNode(node, `${glbId}-root-${index}`);
              });
            }

            // Update isLast for all children
            children.forEach((child, index) => {
              child.isLast = index === children.length - 1;
            });

            return children.map((child) => (
              <RecursiveChildRenderer
                key={child.key}
                item={child.data}
                type={child.type}
                level={level + 1}
                isLast={child.isLast}
                scene={scene}
                findDirectChildrenAssets={findDirectChildrenAssets}
                expandedGLBs={expandedGLBs}
                setExpandedGLBs={setExpandedGLBs}
                select_object={select_object}
                selectObjectInScene={selectObjectInScene}
                selectGLBMesh={selectGLBMesh}
                selectedPop={selectedPop}
                setselectedPop={setselectedPop}
                submenuPosition={submenuPosition}
                setSubmenuPosition={setSubmenuPosition}
                setSelectedGLBMeshPop={setSelectedGLBMeshPop}
                setGLBMeshSubmenuPosition={setGLBMeshSubmenuPosition}
                // Light-specific popup state
                selectedLightPop={selectedLightPop}
                setSelectedLightPop={setSelectedLightPop}
                lightSubmenuPosition={lightSubmenuPosition}
                setLightSubmenuPosition={setLightSubmenuPosition}
                isRenamingLight={isRenamingLight}
                setIsRenamingLight={setIsRenamingLight}
                renamingLight={renamingLight}
                setRenamingLight={setRenamingLight}
                newLightName={newLightName}
                setNewLightName={setNewLightName}
                getAssetIcon={getAssetIcon}
                iconsPath={iconsPath}
                shapesIcon={shapesIcon}
                assetIcons={assetIcons}
                setDraggingAssetId={setDraggingAssetId}
                setHoverTargetAssetId={setHoverTargetAssetId}
                handleDropOnAsset={handleDropOnAsset}
                parentKey={`${parentKey}-${itemId}`}
                glbRootId={effectiveGlbRootId}
                glbMeshes={glbMeshes}
                extractMeshesFromGLB={extractMeshesFromGLB}
                setGLBMeshes={setGLBMeshes}
                // Pass delete handlers
                handleRemoveLight={handleRemoveLight}
                handleRemoveParticle={handleRemoveParticle}
                handleRemoveCamera={handleRemoveCamera}
                handleDuplicate={handleDuplicate}
                deleteAllChildren={deleteAllChildren}
                cleanupParentReferences={cleanupParentReferences}
                dispatch={dispatch}
                store={store}
                assets_data={assets_data}
                light_data={light_data}
                particles={particles}
                selectedObject={selectedObject}
                setSelectedObjectState={setSelectedObjectState}
                outSideRef={outSideRef}
                // Unified handlers
                handleUnifiedMenuClick={handleUnifiedMenuClick}
                handleUnifiedRename={handleUnifiedRename}
                handleUnifiedDuplicate={handleUnifiedDuplicate}
                handleUnifiedDelete={handleUnifiedDelete}
                unifiedPopup={unifiedPopup}
                setUnifiedPopup={setUnifiedPopup}
                unifiedRename={unifiedRename}
                setUnifiedRename={setUnifiedRename}
                handleRenameSubmit={handleRenameSubmit}
                DeleteMesh={DeleteMesh}
                handleVisibilityToggle={handleVisibilityToggle}
                objectt={objectt}
              />
            ));
          })()}
        </div>
      )}
    </React.Fragment>
  );
};
